/**
 * Forecast "Order now" (23 #4, #5) — right under the KPIs: each short part with Short (red), Available,
 * Incoming, lead time, order-by date and supplier (the catalog's, else the last LPO's), a "Create LPO"
 * button that opens New LPO filled in, and check boxes to order several at once. The Short number opens
 * "Why N short?": demand − available − incoming, and how much of it is pieces already delivered on the LPOs
 * that are not in stock.
 *
 * When those delivered-but-not-in-stock pieces explain the WHOLE shortage, the next step is to find them, not to
 * buy them again, so the row's first action is "Check stock" and "Create LPO" stays available as a secondary
 * button. A part ordered from here is marked "On LPO-… · just now"; while /forecast still counts a new LPO as
 * demand as well as incoming (its demand lines carry no `counts_as_demand` flag), the card says the row stays.
 *
 * "Check stock" and the "Why short?" link open "Where is it?" (MaterialTracePanel, `onTrace`) for the part: delivered
 * on which DN, by whom, how much is in store and issued — the Stock list alone shows nothing for a part that is not
 * in stock. Without `onTrace` they link to Inventory › Stock as before.
 *
 * A short part already on a draft LPO that was not sent yet (22 #9, `draft_lpos`) says so on its row; when the
 * drafts hold the whole shortage the row's action is "Open draft" (send it) instead of "Create LPO", so the same
 * pieces are not ordered twice.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FilePlus, FileText, Info, PackageSearch, ShoppingCart, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  CardHead, DateCell, MissingText, SfCard, Tag, TableScroll, numClass, plural, tableClass, tdClass, thClass, useIsPhone,
} from './SfUi';
import { ShortDue } from './ForecastTables';
import { shortageBreakdown, suggestedSupplier } from './forecastModel';
import { draftHref } from './demandModel';

const n = (value) => formatNumber(value);
const inventoryLink = (part) => `/inventory?tab=stock&search=${encodeURIComponent(part)}`;
const traceTarget = (suggestion) => ({ part: suggestion.part_number, description: suggestion.description || '' });

function Row({ label, value, sub, sign, strong, tone }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-[12.5px]">
      <span className={cn('min-w-0', strong ? 'font-bold text-ink' : 'text-ink-2')}>
        {sign ? <span className="mr-1 text-ink-3">{sign}</span> : null}{label}
        {sub ? <span className="block text-[11.5px] font-normal text-ink-3">{sub}</span> : null}
      </span>
      <span className={cn('shrink-0 font-mono tabular-nums', strong ? 'text-[13.5px] font-bold' : 'text-[12.5px] text-ink', tone === 'red' && 'text-status-red-ink')}>{value}</span>
    </div>
  );
}

/** "Why 20 short?" popover on the Short number. `onTrace(target)` opens "Where is it?" for the part. */
export function WhyShort({ suggestion, onTrace }) {
  const [open, setOpen] = useState(false);
  const b = shortageBreakdown(suggestion);
  // A line the API marks `counts_as_demand: false` (an LPO raised from Forecast to fill a shortage) is supply only.
  const demandOnly = b.demandLines.filter((line) => line.counts_as_demand !== false);
  const lpoText = demandOnly.length
    ? demandOnly.map((line) => `${line.lpo_number} ordered ${n(line.ordered_qty)}`).join(' · ')
    : 'open LPO lines';
  const deliveredOn = b.demandLines.filter((line) => Number(line.delivered_qty) > 0);
  const isAre = (count) => (count === 1 ? 'is' : 'are');
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded font-bold tabular-nums text-status-red-ink underline decoration-dotted decoration-2 underline-offset-4 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Why ${suggestion.part_number} is ${n(b.short)} short`}
          data-testid={`why-short-${suggestion.part_number}`}
        >
          {n(b.short)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] rounded-xl border-line bg-surface p-3.5 text-ink shadow-overlay" data-testid={`why-short-popover-${suggestion.part_number}`}>
        <p className="mb-1.5 font-heading text-[13.5px] font-bold">Why {n(b.short)} short?</p>
        {b.hasBreakdown ? (
          <>
            <Row label="Demand" value={n(b.demand)} sub={`${lpoText} · reservations ${n(b.reserved)}`} />
            <Row
              sign="−"
              label="Available in stock"
              value={n(b.available)}
              sub={b.reserved > 0 || b.locations.length > 1
                ? `${n(b.stockOnHand)} on hand${b.locations.length > 1 ? ` (${b.locations.map((loc) => `${loc.location} ${n(loc.qty)}`).join(', ')})` : ''}${b.reserved > 0 ? ` − ${n(b.reserved)} reserved` : ''}`
                : undefined}
            />
            <Row sign="−" label="Still incoming" value={n(b.incoming)} />
            <div className="my-1 border-t border-line-2" />
            <Row sign="=" label="Short" value={n(b.short)} strong tone="red" />
            {b.stockGap ? (
              <div className="mt-2.5 rounded-lg border border-[color:var(--status-amber-bg)] bg-status-amber-bg px-2.5 py-2 text-[12px] text-status-amber-ink" role="note" data-testid={`why-short-gap-${suggestion.part_number}`}>
                <p className="flex items-start gap-1.5">
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span>
                    {deliveredOn.length === 1
                      ? <><span className="font-mono">{deliveredOn[0].lpo_number}</span> shows {n(b.deliveredToStock)} delivered</>
                      : `${n(b.deliveredToStock)} were delivered on ${deliveredOn.length > 1 ? `${deliveredOn.length} LPOs` : 'LPOs'}`}
                    {b.deliveredToSite > 0 ? ' into the warehouse' : ''}, but {n(b.stockOnHand)} {isAre(b.stockOnHand)} in stock under this part no.
                    {b.toSiteKnown ? null : <span className="block">They may be in another store, already issued, or delivered direct to site.</span>}
                  </span>
                </p>
                {onTrace ? (
                  <button
                    type="button"
                    onClick={() => { setOpen(false); onTrace(traceTarget(suggestion)); }}
                    className="ml-5 mt-1 inline-flex items-center gap-1 rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid={`why-short-trace-${suggestion.part_number}`}
                  >
                    <PackageSearch className="h-3.5 w-3.5" aria-hidden="true" />See where they went
                  </button>
                ) : (
                  <Link to={inventoryLink(suggestion.part_number)} className="ml-5 mt-1 inline-block font-semibold text-brand-ink hover:underline">
                    Check Inventory locations
                  </Link>
                )}
              </div>
            ) : null}
            {b.deliveredToSite > 0 && b.gapQty > 0 ? (
              <p className="mt-2 flex items-start gap-1.5 text-[12px] text-ink-2" data-testid={`why-short-site-${suggestion.part_number}`}>
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
                <span>{n(b.deliveredToSite)} of the delivered pieces went direct to site, so they were never in stock — Forecast still counts them as missing.</span>
              </p>
            ) : null}
            {b.gapQty > 0 ? (
              <p className="mt-2 text-[12px] font-semibold text-ink" data-testid={`why-short-verdict-${suggestion.part_number}`}>
                {b.coveredByGap && b.toSiteKnown && b.missingFromStock === 0
                  ? `All ${n(b.short)} short went direct to site — they are on site, not missing. Nothing to order for them.`
                  : b.coveredByGap
                    ? `All ${n(b.short)} short ${isAre(b.short)} delivered pieces that are not in stock — find them before ordering more.`
                    : `${n(b.gapQty)} of the ${n(b.short)} short ${isAre(b.gapQty)} delivered pieces not in stock — ${n(b.buyQty)} left to order.`}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-[12.5px] text-ink-3">Demand {n(Number(b.short) + b.available + b.incoming)} − available {n(b.available)} − incoming {n(b.incoming)} = {n(b.short)}.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

function SupplierCell({ suggestion }) {
  const supplier = suggestedSupplier(suggestion);
  if (!supplier.name) return <MissingText>Supplier missing</MissingText>;
  return (
    <span className="block min-w-0">
      <span className="block truncate font-semibold text-ink">{supplier.name}</span>
      {supplier.source === 'last_lpo' ? (
        <span className="block truncate text-[11.5px] text-ink-3">
          from last LPO{suggestion.in_catalog === false ? <> · <span className="font-semibold text-status-amber-ink">not in catalog</span></> : null}
        </span>
      ) : null}
    </span>
  );
}

/** The drafts (not sent yet) that already hold a short part: { drafts, qty, covers } — covers = they hold all of it. */
export function draftCover(suggestion) {
  const drafts = (Array.isArray(suggestion?.draft_lpos) ? suggestion.draft_lpos : []).filter((draft) => draft?.lpo_number);
  const qty = drafts.reduce((sum, draft) => sum + (Number(draft.qty) || 0), 0);
  return { drafts, qty, covers: drafts.length > 0 && qty >= (Number(suggestion?.shortage_qty) || 0) };
}

/** "On draft LPO-2026-001 (Approved) · 24" under the part — a short part that is already being ordered. */
function DraftNote({ suggestion }) {
  const cover = draftCover(suggestion);
  if (!cover.drafts.length) return null;
  const first = cover.drafts[0];
  return (
    <span className="mt-0.5 flex min-w-0 items-center gap-1 text-[11.5px] font-semibold text-status-navy-ink" data-testid={`order-now-draft-${suggestion.part_number}`}>
      <FileText className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">
        On draft <Link to={draftHref(first.lpo_number)} className="font-mono hover:underline">{first.lpo_number}</Link>
        {first.status ? ` (${first.status})` : ''} · {n(cover.qty)}{cover.drafts.length > 1 ? ` on ${cover.drafts.length} drafts` : ''}
        {cover.covers ? ' — send it' : ` — ${n(Math.max(0, (Number(suggestion.shortage_qty) || 0) - cover.qty))} more to order`}
      </span>
    </span>
  );
}

/** Row actions: "Create LPO", or — when the whole shortage is a stock gap — "Check stock" first; when drafts not sent
 * yet hold all of it — "Open draft". */
function RowAction({ suggestion, onCreateLpo, onTrace, ordered, phone = false }) {
  const b = shortageBreakdown(suggestion);
  const part = suggestion.part_number;
  const create = () => onCreateLpo([suggestion]);
  const cover = draftCover(suggestion);
  const marker = ordered ? (
    <span className={cn('block text-[11.5px] text-ink-3', phone ? 'mt-1.5' : 'mt-1')} data-testid={`order-now-ordered-${part}`}>
      On <span className="font-mono">{ordered}</span> · just now
    </span>
  ) : null;
  if (cover.covers) {
    return (
      <>
        <Button variant="outline" size="sm" asChild className={phone ? 'mt-2 w-full' : undefined}>
          <Link to={draftHref(cover.drafts[0].lpo_number)} data-testid={`order-now-open-draft-${part}`} title={`On draft ${cover.drafts[0].lpo_number} — send it to the supplier from Procurement`}>
            <FileText aria-hidden="true" /> Open draft
          </Link>
        </Button>
        {marker}
      </>
    );
  }
  if (b.coveredByGap) {
    return (
      <>
        <span className={cn('flex items-center gap-1.5', phone ? 'mt-2' : 'justify-end')}>
          {onTrace ? (
            <Button
              variant="outline"
              size="sm"
              className={phone ? 'flex-1' : undefined}
              onClick={() => onTrace(traceTarget(suggestion))}
              title={`All ${n(b.short)} short are pieces delivered on the LPOs that are not in stock — see where they went`}
              data-testid={`order-now-check-${part}`}
            >
              <PackageSearch aria-hidden="true" /> Check stock
            </Button>
          ) : (
            <Button variant="outline" size="sm" asChild className={phone ? 'flex-1' : undefined}>
              <Link to={inventoryLink(part)} data-testid={`order-now-check-${part}`} title={`All ${n(b.short)} short are pieces delivered on the LPOs that are not in stock`}>
                <PackageSearch aria-hidden="true" /> Check stock
              </Link>
            </Button>
          )}
          <Button
            variant="ghost"
            size={phone ? 'sm' : 'icon-sm'}
            onClick={create}
            title="Create LPO anyway"
            aria-label={`Create LPO for ${part} anyway`}
            className="text-ink-2"
            data-testid={`order-now-create-${part}`}
          >
            <FilePlus aria-hidden="true" />{phone ? ' Create LPO' : null}
          </Button>
        </span>
        {marker}
      </>
    );
  }
  return (
    <>
      <Button variant="outline" size="sm" className={phone ? 'mt-2 w-full' : undefined} onClick={create} data-testid={`order-now-create-${part}`}>
        <FilePlus aria-hidden="true" /> Create LPO
      </Button>
      {marker}
    </>
  );
}

/**
 * props: suggestions, cardRef, hiddenCount (short outside the filters), canCreateLpo,
 * selected (Set of part numbers), onSelectedChange(Set), onCreateLpo(suggestions[]),
 * ordered (Map part → LPO number created from this page just now), onTrace({ part, description }) — "Where is it?".
 */
export function OrderNowCard({ suggestions, now, cardRef, hiddenCount = 0, canCreateLpo = false, selected = new Set(), onSelectedChange, onCreateLpo, ordered, onTrace }) {
  const isPhone = useIsPhone();
  const selectable = canCreateLpo && Boolean(onSelectedChange);
  const picked = suggestions.filter((s) => selected.has(s.part_number));
  const allPicked = suggestions.length > 0 && picked.length === suggestions.length;
  const orderedMap = ordered instanceof Map ? ordered : new Map();
  // Older /forecast counts a new LPO as demand AND incoming, so a part ordered here stays listed: say so. A backend
  // that nets LPOs raised from Forecast marks each demand line with `counts_as_demand`.
  const netsForecastLpos = suggestions.some((s) => (Array.isArray(s.demand_lines) ? s.demand_lines : []).some((line) => 'counts_as_demand' in line));
  const anyOrdered = !netsForecastLpos && suggestions.some((s) => orderedMap.has(s.part_number));
  const gapCount = suggestions.filter((s) => shortageBreakdown(s).coveredByGap).length;
  const toggle = (part) => {
    const next = new Set(selected);
    if (next.has(part)) next.delete(part); else next.add(part);
    onSelectedChange(next);
  };
  const toggleAll = () => onSelectedChange(allPicked ? new Set() : new Set(suggestions.map((s) => s.part_number)));

  const headRight = suggestions.length && selectable ? (
    picked.length ? (
      <>
        <span className="text-[12.5px] text-ink-3">{plural(picked.length, 'part')} selected</span>
        <Button variant="ghost" size="sm" onClick={() => onSelectedChange(new Set())}>Clear</Button>
        <Button size="sm" variant="outline" onClick={() => onCreateLpo(picked)} data-testid="order-now-create-selected">
          <FilePlus aria-hidden="true" /> Create LPO for {plural(picked.length, 'part')}
        </Button>
      </>
    ) : (
      <Button variant="ghost" size="sm" onClick={toggleAll} data-testid="order-now-select-all">Select all</Button>
    )
  ) : null;

  const sub = suggestions.length
    ? `${plural(suggestions.length, 'part')} short${gapCount ? ` · ${gapCount === suggestions.length ? (gapCount === 1 ? 'it is' : 'all are') : `${formatNumber(gapCount)} ${gapCount === 1 ? 'is' : 'are'}`} delivered but not in stock` : ''}`
    : hiddenCount > 0 ? 'none in this view' : 'nothing short';

  return (
    <div ref={cardRef} className="scroll-mt-24">
      <SfCard data-testid="order-suggestions">
        <CardHead icon={ShoppingCart} iconTone="amber" title="Order now" sub={sub} right={headRight} />
        {!suggestions.length ? (
          hiddenCount > 0 ? (
            <EmptyState layout="inline" icon={ShoppingCart} title="Nothing to order in this view" text={`${plural(hiddenCount, 'part')} short outside these filters — clear them to see ${hiddenCount === 1 ? 'it' : 'them'}.`} />
          ) : (
            <EmptyState layout="inline" icon={ShoppingCart} title="No shortages" text="Stock plus incoming LPO quantities cover the current demand." />
          )
        ) : isPhone ? (
          <ul className="divide-y divide-line-2">
            {suggestions.map((s) => {
              const b = shortageBreakdown(s);
              return (
                <li key={s.part_number} className="px-4 py-2.5" data-testid={`order-now-row-${s.part_number}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block font-mono text-[12.5px] font-semibold text-ink">{s.part_number}</span>
                      <span className="block truncate text-[12px] text-ink-3">{s.description || '—'}</span>
                      <DraftNote suggestion={s} />
                    </span>
                    <span className="shrink-0 text-right text-[12px] text-ink-3">short <span className="text-[15px]"><WhyShort suggestion={s} onTrace={onTrace} /></span></span>
                  </div>
                  <p className="mt-1 text-[12px] text-ink-3" data-testid={`order-now-due-${s.part_number}`}>
                    Order by <ShortDue value={s.recommended_order_date} now={now} /> · {formatNumber(s.lead_time_days)} d lead{s.lead_time_is_default ? ' (default)' : ''} · {suggestedSupplier(s).name || <MissingText>Supplier missing</MissingText>}
                  </p>
                  {b.coveredByGap ? (
                    <p className="mt-1 flex items-start gap-1.5 text-[12px] font-semibold text-status-amber-ink">
                      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      All {formatNumber(b.short)} were delivered but are not in stock — check stock before ordering.
                    </p>
                  ) : null}
                  {canCreateLpo ? (
                    <RowAction suggestion={s} onCreateLpo={onCreateLpo} onTrace={onTrace} ordered={orderedMap.get(s.part_number)} phone />
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <TableScroll>
            <table className={cn(tableClass, 'min-w-[980px] table-fixed')}>
              <colgroup>
                {selectable ? <col style={{ width: 44 }} /> : null}
                <col /><col style={{ width: 76 }} /><col style={{ width: 88 }} /><col style={{ width: 88 }} />
                <col style={{ width: 112 }} /><col style={{ width: 128 }} /><col style={{ width: 196 }} />
                {canCreateLpo ? <col style={{ width: 168 }} /> : null}
              </colgroup>
              <thead>
                <tr>
                  {selectable ? (
                    <th className={cn(thClass, 'pr-0')}>
                      <TableCheckbox checked={allPicked} indeterminate={picked.length > 0 && !allPicked} onCheckedChange={toggleAll} aria-label={allPicked ? 'Clear selection' : 'Select all short parts'} />
                    </th>
                  ) : null}
                  <th className={thClass}>Part</th>
                  <th className={cn(thClass, 'text-right')}>Short</th>
                  <th className={cn(thClass, 'text-right')}>Available</th>
                  <th className={cn(thClass, 'text-right')}>Incoming</th>
                  <th className={thClass}>Lead time</th>
                  <th className={thClass}>Order by</th>
                  <th className={thClass}>Supplier</th>
                  {canCreateLpo ? <th className={thClass}><span className="sr-only">Actions</span></th> : null}
                </tr>
              </thead>
              <tbody>
                {suggestions.map((s) => {
                  const on = selected.has(s.part_number);
                  const gap = shortageBreakdown(s).coveredByGap;
                  return (
                    <tr key={s.part_number} className={cn(on ? '[&>td]:bg-brand-soft' : 'hover:[&>td]:bg-surface-hover')} data-testid={`order-now-row-${s.part_number}`}>
                      {selectable ? (
                        <td className={cn(tdClass, 'pr-0')}>
                          <TableCheckbox checked={on} onCheckedChange={() => toggle(s.part_number)} aria-label={`Select ${s.part_number}`} />
                        </td>
                      ) : null}
                      <td className={tdClass}>
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="font-mono text-[12.5px] font-semibold text-ink">{s.part_number}</span>
                          {s.category ? <Tag>{s.category}</Tag> : null}
                        </span>
                        <span className="block truncate text-[12px] text-ink-3">{s.description || '—'}</span>
                        <DraftNote suggestion={s} />
                      </td>
                      <td className={cn(tdClass, numClass)}>
                        <span className="inline-flex items-center justify-end gap-1">
                          {gap ? (
                            <span
                              role="img"
                              aria-label="Delivered but not in stock"
                              title="All of these were delivered on the LPOs but are not in stock — check stock before ordering"
                              className="inline-flex text-status-amber-ink"
                              data-testid={`order-now-gap-${s.part_number}`}
                            >
                              <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
                            </span>
                          ) : null}
                          <WhyShort suggestion={s} onTrace={onTrace} />
                        </span>
                      </td>
                      <td className={cn(tdClass, numClass)}>{formatNumber(s.current_available)}</td>
                      <td className={cn(tdClass, numClass)}>{formatNumber(s.incoming_qty)}</td>
                      <td className={tdClass}>
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          {formatNumber(s.lead_time_days)} d
                          {s.lead_time_is_default ? <Tag title="The catalog has no lead time for this part — the 14-day default is used">default</Tag> : null}
                        </span>
                      </td>
                      <td className={cn(tdClass, 'py-1.5')}><DateCell value={s.recommended_order_date} now={now} soonDays={30} /></td>
                      <td className={cn(tdClass, 'text-[12.5px]')}><SupplierCell suggestion={s} /></td>
                      {canCreateLpo ? (
                        <td className={cn(tdClass, 'py-1.5 text-right')}>
                          <RowAction suggestion={s} onCreateLpo={onCreateLpo} onTrace={onTrace} ordered={orderedMap.get(s.part_number)} />
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
        {suggestions.length ? (
          <div className="space-y-1 border-t border-line-2 px-4 py-2.5 text-[12px] text-ink-3 sm:px-[18px]">
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Short = LPO demand + reservations − in stock − still incoming. Tap a number to see the sum.
              {suggestions.some((s) => s.lead_time_is_default) ? <span className="sm:ml-auto">Lead time 14 d is the default until the catalog has one</span> : null}
            </p>
            {anyOrdered ? (
              <p className="pl-5" data-testid="order-now-ordered-note">
                Parts ordered here stay listed: Forecast does not subtract a new LPO from the shortage yet.
              </p>
            ) : null}
          </div>
        ) : null}
      </SfCard>
    </div>
  );
}
